import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PrefsProvider, usePrefs } from "@/context/PrefsContext";
import AppShell from "@/components/AppShell";
import { LogoMark } from "@/components/Logo";
import { CookieBanner } from "@/components/CookieBanner";
import Login from "@/pages/Login";
import { ForgotPassword, ResetPassword, VerifyEmail } from "@/pages/AuthFlows";
import Legal from "@/pages/Legal";
import Dashboard from "@/pages/Dashboard";
import Markets from "@/pages/Markets";
import Trade from "@/pages/Trade";
import Portfolio from "@/pages/Portfolio";
import WalletPage from "@/pages/Wallet";
import Notifications from "@/pages/Notifications";
import Profile from "@/pages/Profile";
import Security from "@/pages/Security";
import AdminOverview from "@/pages/admin/Overview";
import AdminClients from "@/pages/admin/Clients";
import AdminClientDetail from "@/pages/admin/ClientDetail";
import AdminFinance from "@/pages/admin/Finance";
import AdminTrading from "@/pages/admin/Trading";
import AdminDocuments from "@/pages/admin/Documents";
import AdminNotifications from "@/pages/admin/Announcements";
import AdminAssets from "@/pages/admin/Assets";
import AdminReports from "@/pages/admin/Reports";
import AdminAdmins from "@/pages/admin/Admins";

const Loading = () => (
  <div className="grid min-h-screen place-items-center" data-testid="app-loading">
    <div className="flex flex-col items-center gap-4 text-sm text-muted-foreground"><LogoMark size={72} className="animate-pulse rounded-2xl" />Carregando…</div>
  </div>
);

const Guard = ({ role, children }) => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/entrar" replace />;
  if (role && user.role !== role) return <Navigate to={user.role === "admin" ? "/admin" : "/painel"} replace />;
  return children;
};

const Home = () => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/entrar" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/painel"} replace />;
};

const SecurityShell = () => {
  const { user } = useAuth();
  return <AppShell admin={user?.role === "admin"} />;
};

const ThemedToaster = () => {
  const { theme } = usePrefs();
  return <Toaster theme={theme} position="top-right" richColors closeButton />;
};

function App() {
  return (
    <PrefsProvider>
      <AuthProvider>
        <TooltipProvider delayDuration={150}>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/entrar" element={<Login />} />
              <Route path="/esqueci-senha" element={<ForgotPassword />} />
              <Route path="/redefinir-senha" element={<ResetPassword />} />
              <Route path="/verificar-email" element={<VerifyEmail />} />
              <Route path="/legal/:doc" element={<Legal />} />
              <Route element={<Guard role="client"><AppShell /></Guard>}>
                <Route path="/painel" element={<Dashboard />} />
                <Route path="/mercados" element={<Markets />} />
                <Route path="/negociar/:symbol" element={<Trade />} />
                <Route path="/carteira" element={<Portfolio />} />
                <Route path="/financeiro" element={<WalletPage />} />
                <Route path="/notificacoes" element={<Notifications />} />
                <Route path="/perfil" element={<Profile />} />
              </Route>
              <Route element={<Guard><SecurityShell /></Guard>}>
                <Route path="/seguranca" element={<Security />} />
              </Route>
              <Route element={<Guard role="admin"><AppShell admin /></Guard>}>
                <Route path="/admin" element={<AdminOverview />} />
                <Route path="/admin/clientes" element={<AdminClients />} />
                <Route path="/admin/clientes/:id" element={<AdminClientDetail />} />
                <Route path="/admin/financeiro" element={<AdminFinance />} />
                <Route path="/admin/operacoes" element={<AdminTrading />} />
                <Route path="/admin/documentos" element={<AdminDocuments />} />
                <Route path="/admin/comunicados" element={<AdminNotifications />} />
                <Route path="/admin/ativos" element={<AdminAssets />} />
                <Route path="/admin/relatorios" element={<AdminReports />} />
                <Route path="/admin/administradores" element={<AdminAdmins />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <CookieBanner />
          </BrowserRouter>
          <ThemedToaster />
        </TooltipProvider>
      </AuthProvider>
    </PrefsProvider>
  );
}

export default App;
